import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import {
  mutationOptions,
  withError,
  withInvalidate,
} from '@/react-tools/react-query';
import {
  AutoUpdateResponse,
  RelativePathModel,
} from '@/react/portainer/gitops/types';
import { buildUrl } from '@/react/edge/edge-stacks/queries/buildUrl';
import {
  DeploymentType,
  EdgeStack,
  StaggerConfig,
} from '@/react/edge/edge-stacks/types';
import { EdgeGroup } from '@/react/edge/edge-groups/types';
import { Registry } from '@/react/portainer/registries/types/registry';

import { queryKeys } from '../../../queries/query-keys';
import { DeployerOptionsPayload } from '../../../components/DeployerOptions';

export interface UpdateEdgeStackGitPayload {
  id: EdgeStack['Id'];
  autoUpdate: AutoUpdateResponse | null;
  refName: string;
  groupIds: EdgeGroup['Id'][];
  deploymentType: DeploymentType;
  repullImageAndRedeploy: boolean;
  updateVersion: boolean;
  registries?: Array<Registry['Id']>;
  prePullImage: boolean;
  retryDeploy: boolean;
  retryPeriod: number;
  staggerConfig: StaggerConfig;
  deployerOptions: DeployerOptionsPayload;
  relativePath: RelativePathModel;
  // Helm-specific update options. Only applies to Helm git deployments.
  helmConfig?: {
    chartPath?: string;
    valuesFiles?: string[];
    atomic?: boolean;
    timeout?: string;
    namespace?: string;
  };
}

export function useUpdateEdgeStackGitMutation() {
  const queryClient = useQueryClient();

  return useMutation(
    updateEdgeStackGit,
    mutationOptions(
      withError('Failed updating stack'),
      withInvalidate(queryClient, [queryKeys.base()])
    )
  );
}

async function updateEdgeStackGit({
  id,
  helmConfig,
  ...payload
}: UpdateEdgeStackGitPayload) {
  try {
    const apiPayload = {
      ...payload,
      ...(helmConfig && {
        HelmConfig: {
          ChartPath: helmConfig.chartPath,
          ValuesFiles: helmConfig.valuesFiles,
          Atomic: helmConfig.atomic,
          Timeout: helmConfig.timeout,
          Namespace: helmConfig.namespace,
        },
      }),
    };

    await axios.put(buildUrl(id, 'git'), apiPayload);
  } catch (err) {
    throw parseAxiosError(err as Error, 'Failed updating stack');
  }
}
